import {
  Alert, Box, Button, Grid, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme,
} from '@mui/material';
import { Trash01, Plus } from '../../../assets';
import { useContext, useState, useEffect } from 'react';
import { StockCountZonesContext, StockCountZonesContextValues } from '../contexts/StockCountZonesContext';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const StockCountZonesItems = () => {
  const {
    t,
    isLoading,
    handleAddBulkItemClick,
    handleAddItemsClick,
    deleteItemRow,
    itemsData,
    reorderItems,
    categoryData,
    zoneName,
  } = useContext(StockCountZonesContext) as StockCountZonesContextValues;

  const [items, setItems] = useState(itemsData);

  useEffect(() => {
    setItems(itemsData);
  }, [itemsData]);

  const theme = useTheme();
  const hasItems = items.length > 0;

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const newItems = [...items];
    const [removed] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, removed);
    setItems(newItems);
    reorderItems(newItems);
  };

  if (isLoading) return null;

  return (
    <Box sx={{ border: '2px solid', borderRadius: '4px', borderColor: theme.palette.primary[500] }}>
      <Grid container
        sx={{ pt: '12px', pb: '6px', px: '8px' }}>
        <Grid item
          xs={12}
          sm={3}>
          <Typography variant="textMD"
            sx={{ fontWeight: 'bold' }}>{t('items.title')}</Typography>
        </Grid>
        <Grid item
          xs={12}
          sm={9}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
            {hasItems && (
              <Button
                size="lg"
                variant="bad"
                onClick={() => deleteItemRow()}
                startIcon={<Trash01 />}
              >
                {t('items.deleteAll')}
              </Button>
            )}
            <Button size="lg"
              variant="primary"
              onClick={handleAddBulkItemClick}
              startIcon={<Plus />}>
              {t('items.addBulk')}
            </Button>
            <Button size="lg"
              variant="primary"
              onClick={handleAddItemsClick}
              startIcon={<Plus />}>
              {t('items.add')}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ pt: '10px' }}>
        {hasItems ? (
          <TableContainer sx={{ overflowY: 'auto' }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center"
                      width="60px"></TableCell>
                    <TableCell align="center">Row Number</TableCell>
                    <TableCell align="left">{t('items.itemNumber')}</TableCell>
                    <TableCell align="left">{t('items.description')}</TableCell>
                    <TableCell align="left">{t('items.zone')}</TableCell>
                    <TableCell align="right">{t('items.remove')}</TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="items-list">
                  {(provided) => (
                    <TableBody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {items.map((item, index) => (
                        <Draggable
                          key={index.toString()}
                          draggableId={index.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                display: 'table-row',
                              }}
                            >
                              <TableCell align="center">
                                <Box
                                  {...provided.dragHandleProps}
                                  sx={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  <DragIndicatorIcon />
                                </Box>
                              </TableCell>
                              <TableCell align="center">{index + 1}</TableCell>
                              <TableCell>{item.itemNumber}</TableCell>
                              <TableCell>{item.itemDescription}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  {(item.zoneNames?.includes(zoneName) ?? false) ? (
                                    <Tooltip title={t('items.currentZone')}
                                      arrow>
                                      <Box component="span"
                                        sx={{ border: '1px solid #4caf50', color: '#4caf50', borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                        {zoneName?.trim() ? zoneName : t('items.unnamedZone')}
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title={t('items.pendingChangeTooltip')}
                                      arrow>
                                      <Box component="span"
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid #ed6c02', color: '#ed6c02', borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                        <Box component="span"
                                          sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ed6c02' }} />
                                        {zoneName?.trim() ? zoneName : t('items.unnamedZone')}
                                      </Box>
                                    </Tooltip>
                                  )}
                                  {(() => {
                                    const otherZones = (item.zoneNames ?? []).filter(z => z !== zoneName);
                                    if (otherZones.length === 0) return null;
                                    if (otherZones.length === 1) {
                                      return (
                                        <Box component="span"
                                          sx={{ border: `1px solid ${theme.palette.primary.main}`, color: theme.palette.primary.main, borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                          {otherZones[0]}
                                        </Box>
                                      );
                                    }
                                    return (
                                      <Tooltip title={otherZones.join(', ')}
                                        arrow>
                                        <Box component="span"
                                          sx={{ border: `1px solid ${theme.palette.primary.main}`, color: theme.palette.primary.main, borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                          {`+${otherZones.length} ${t('items.zones')}`}
                                        </Box>
                                      </Tooltip>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton aria-label='delete'
                                  onClick={() => deleteItemRow(item.itemNumber ?? '')}
                                  size="small"
                                >
                                  <Trash01 style={{ color: 'red' }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </TableContainer>
        ) : (
          <Alert severity="info"
            variant="outlined"
            sx={{ mb: '12px', mx: '8px' }}>
            {categoryData.length > 0 ? t('items.noAdditionalItems') : t('items.noItems')}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default StockCountZonesItems;